'use client';

import Link from 'next/link';
import { styled } from 'styled-components';

import { trackLink } from '@lib/tracking';
import type { SafeUrl } from '@types';

/**
 * Decorative back-arrow. `aria-hidden` (applied at the call site) keeps it out
 * of the accessibility tree so the link's accessible name stays exactly
 * “Back to blog”, not “← Back to blog”.
 */
const Arrow = styled.span`
  display: inline-block;
  padding-inline-end: ${({ theme }) => theme.spacing[0.25]};
  transition: transform var(--global-motion);
`;

/**
 * Note: looks like a TextLink, but too much CSS is different, so it doesn't
 * make much sense to use those styles here.
 */
const BackLink = styled(Link)`
  color: ${({ theme }) => theme.colors.blue800};
  display: inline-block;
  justify-self: start;
  flex-shrink: 0;
  font-family: var(--font-inter);
  font-size: ${({ theme }) => theme.typography.fontSize.bodyXs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.headingTwo};
  line-height: 1.4;
  white-space: nowrap;

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${({ theme }) => theme.typography.fontSize.bodySm};
  }

  /* Keyboard-focus affordance; mouse users get the same nudge on :hover. */
  &:hover ${Arrow}, &:focus-visible ${Arrow} {
    transform: translateX(-${({ theme }) => theme.spacing[0.25]});
  }
`;

/**
 * `← Back to blog` link to the blog home, with a tracked click. Shared by the
 * article/author breadcrumbs and the category hero. Accepts `className` so it
 * can be positioned by its parent (e.g. a breadcrumb grid cell).
 */
const BackToBlogLink = ({ className }: { className?: string }) => (
  <BackLink
    className={className}
    href={'/blog' as SafeUrl}
    onClick={() => trackLink('blog-breadcrumb', 'back-to-blog')}
  >
    <Arrow aria-hidden="true">←</Arrow>
    Back to blog
  </BackLink>
);

export default BackToBlogLink;
